// Utility functions for product store
// This allows testing internal functions without exposing them in the public API

import { useConfigStore } from '@/shared/pinia/modules/config';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import { formattedPrice } from '@/shared/utils/shopify/money-formatter';

export const productStoreInternals = () => {
  const store = useProductStore();

  const updateDOMPricesAndSavings = () => {
    const optionsStore = useProductOptionsStore();
    const configStore = useConfigStore();

    const productPriceBlock = document.querySelector<HTMLElement>('.js-price');
    const productPriceBefore =
      document.querySelector<HTMLElement>('.js-price-before');
    const productPriceWrapper =
      document.querySelector<HTMLElement>('.js-price-wrapper');
    const productPointsWrapper =
      document.querySelector<HTMLElement>('.js-points-wrapper');
    const productPriceSave =
      document.querySelector<HTMLElement>('.js-price-save');
    const productPriceFromLabel = document.querySelectorAll<HTMLElement>(
      '.js-price-from-label'
    );

    // 1. Update unit price and points
    if (store.price.unit) {
      if (productPriceWrapper) {
        productPriceWrapper.innerHTML = formattedPrice(store.price.unit ?? '');
      }

      if (productPointsWrapper) {
        productPointsWrapper.innerHTML = configStore
          .calculatedPoints(store.price.unit)
          .toString();
      }
    }

    const thereAreSavings = store.price.savings && store.price.before;

    // 2. Update price block
    if (productPriceBlock) {
      if (thereAreSavings) {
        productPriceBlock.classList.add('special');
      } else {
        productPriceBlock.classList.remove('special');
      }
    }

    // 3. Update discount prices
    // 3.1. update before price
    if (productPriceBefore) {
      if (thereAreSavings && store.price.before) {
        productPriceBefore.classList.remove('hidden');
        productPriceBefore.innerHTML = formattedPrice(store.price.before);
      } else {
        productPriceBefore.classList.add('hidden');
      }
    }

    // 3.2. update saving price
    if (productPriceSave) {
      if (store.price.savings) {
        productPriceSave.classList.remove('invisible');
        const spanToUpdate = productPriceSave.querySelector('span');
        if (spanToUpdate) {
          spanToUpdate.innerHTML = formattedPrice(store.price.savings);
        }
      } else {
        productPriceSave.classList.add('invisible');
      }
    }

    // 4. Update "from" label
    if (productPriceFromLabel.length > 0) {
      if (optionsStore.activeOption) {
        productPriceFromLabel.forEach((el) => el.classList.add('hidden'));
      } else {
        productPriceFromLabel.forEach((el) => el.classList.remove('hidden'));
      }
    }
  };

  const calculateNewPricesByQuantityChange = () => {
    const quantityStore = useProductQuantitiesStore();
    const optionsStore = useProductOptionsStore();
    const selectedBulkDiscount = quantityStore.selectedBulkDiscount();

    // unit price will always be initial unit price, with no discount, this resets any state that existed before
    store.price.unit = store.price.initial_unit;
    store.price.before = null;
    store.price.savings = null;

    if (!selectedBulkDiscount) return;

    let variant = optionsStore.activeOption;

    if (!variant) {
      const targetVariantId = window.Hobbii?.productPage?.targetVariantId;
      variant = targetVariantId
        ? optionsStore.options.find(
            (variant) => variant.id === targetVariantId
          ) || null
        : optionsStore.options[0];
    }

    if (selectedBulkDiscount.discount_percentage && variant) {
      store.price.unit = selectedBulkDiscount.price_unit || 0;
      store.price.before = variant.priceBefore || variant.compare_at_price;
      store.price.initial_unit = variant.price;
      store.price.savings =
        (selectedBulkDiscount.price_before || 0) -
        (selectedBulkDiscount.price_unit || 0);
    } else {
      store.price.before = null;
      store.price.savings = null;
    }
  };

  const calculateNewPricesByVariantChange = (option: {
    priceBefore: number | null;
    price: number | null;
  }) => {
    const isOnDiscount = option.priceBefore !== option.price;

    store.price.initial_unit = option.price;

    if (isOnDiscount) {
      store.price.unit = option.price;
      store.price.before = option.priceBefore;
      store.price.savings = (option.priceBefore || 0) - (option.price || 0);
    } else {
      store.price.unit = option.priceBefore;
      store.price.before = null;
      store.price.savings = null;
    }
  };

  return {
    calculateNewPricesByQuantityChange,
    calculateNewPricesByVariantChange,
    updateDOMPricesAndSavings
  };
};
